<main class="p-4 p-xxl-5">
  <section class="card border rounded-3 overflow-hidden">
    <div class="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
      <div class="d-inline-flex align-items-center flex-wrap border rounded-2 bg-light p-1" role="group" :aria-label="`${page_title} filters`">
        <button
          v-for="filter in filters"
          :key="filter.label"
          type="button"
          @click="set_active_filter(filter.tone)"
          :class="[
            'btn btn-sm d-inline-flex align-items-center gap-2 px-3 py-1',
            active_filter === filter.tone ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'
          ]"
        >
          <span>{{ filter.label }}</span>
          <span v-if="filter.count">{{ filter.count }}</span>
        </button>
      </div>

      <div class="d-flex align-items-center justify-content-end flex-wrap gap-2">
        <label class="onboarding-queue-search d-inline-flex align-items-center">
          <i class="bi bi-search" aria-hidden="true"></i>
          <input v-model="table_search" type="search" placeholder="Search table" @input="reset_table_page">
        </label>
        <button type="button" class="btn btn-sm bg-white border rounded-2 d-inline-flex align-items-center gap-2 fw-semibold px-3 py-1">
          <i class="bi bi-funnel"></i>
          <span>Filters</span>
        </button>
        <button type="button" class="btn btn-sm bg-white border rounded-2 d-inline-flex align-items-center gap-2 fw-semibold px-3 py-1">
          <i class="bi bi-download"></i>
          <span>Export</span>
        </button>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th class="text-uppercase text-muted small fw-semibold py-3 ps-4">Case</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Journey</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Progress</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Risk</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Stage</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Owner</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">ETA</th>
            <th class="text-uppercase text-muted small fw-semibold py-3 pe-4">Activation</th>
            <th class="text-uppercase text-muted small fw-semibold py-3 pe-4">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="is_loading_cases">
            <td colspan="9" class="text-center text-muted py-5">Loading cases...</td>
          </tr>
          <tr v-else-if="cases_error">
            <td colspan="9" class="py-4">
              <div class="alert alert-warning mb-0">{{ cases_error }}</div>
            </td>
          </tr>
          <tr v-else-if="filtered_rows.length === 0">
            <td colspan="9" class="text-muted py-4 ps-4">No {{ page_title.toLowerCase() }} cases found.</td>
          </tr>
          <tr
            v-else
            v-for="row in paginated_rows"
            :key="row.id"
            class="cursor-pointer"
            tabindex="0"
            @click="open_case_summary(row)"
            @keyup.enter="open_case_summary(row)"
          >
            <td class="ps-4 py-3">
              <div class="fw-bold text-black">{{ row.case_name }}</div>
              <div class="text-muted small mt-1">{{ row.case_detail }}</div>
            </td>
            <td class="py-3">
              <span class="d-inline-flex align-items-center gap-2">
                <span class="rounded-circle bg-info d-inline-block p-1"></span>
                <span>{{ row.journey }}</span>
              </span>
            </td>
            <td class="py-3">
              <div class="d-flex align-items-center gap-2">
                <div class="progress flex-grow-1 onboarding-progress-track-compact">
                  <div
                    :class="['progress-bar', progress_bar_class(row.progress_tone)]"
                    :style="progress_bar_style(row.progress)"
                  ></div>
                </div>
                <span class="small fw-semibold text-muted">{{ row.progress }}%</span>
              </div>
            </td>
            <td class="py-3">
              <span :class="['badge rounded-pill fw-normal', risk_badge_class(row.risk_tone)]">
                {{ row.risk }}
              </span>
            </td>
            <td class="py-3">
              <span :class="['badge rounded-pill fw-normal', status_badge_class(row.status_tone)]">
                {{ row.status }}
              </span>
            </td>
            <td class="py-3">{{ row.reviewer }}</td>
            <td class="pe-4 py-3">
              {{ row.eta }}
            </td>
            <td class="pe-4 py-3">
              <span :class="['badge rounded-pill fw-normal', status_badge_class(row.activation_tone)]">
                {{ row.activation }}
              </span>
            </td>
            <td class="pe-4 py-3">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary rounded-pill d-inline-flex align-items-center gap-2 fw-semibold px-3"
                @click.stop="open_case_form(row, $event)"
              >
                <span>View</span>
                <i class="bi bi-arrow-right"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <TablePagination
      :page="table_page"
      :page-size="table_page_size"
      :page-size-options="table_page_size_options"
      :total-rows="table_total_rows"
      :page-start="table_page_start"
      :page-end="table_page_end"
      :total-pages="table_total_pages"
      @update:page="set_table_page"
      @update:page-size="set_table_page_size"
    />
  </section>

  <div
    v-if="selected_case_summary"
    class="onboarding-case-detail-backdrop"
    @click="close_case_summary"
  ></div>

  <aside
    v-if="selected_case_summary"
    :class="['onboarding-case-detail-drawer bg-white shadow-lg', { 'is-fullscreen': is_summary_detail_fullscreen }]"
  >
    <header class="border-bottom p-4">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="text-uppercase text-muted fw-semibold small mb-3">
            {{ selected_case_summary.journey || 'Onboarding' }} onboarding · {{ page_title }} gate
          </div>
          <h2 class="h5 fw-bold text-black mb-2">{{ selected_case_summary.name || 'Onboarding case' }}</h2>
          <p class="mb-0 text-muted">{{ selected_case_summary_subtitle() }}</p>
        </div>

        <div class="d-flex align-items-center gap-3 flex-shrink-0">
          <span :class="['badge rounded-pill fw-normal', status_badge_class(selected_case_summary.activation_tone || 'warning')]">
            <span class="me-1">&bull;</span>{{ selected_case_summary.activation || selected_case_summary.stage || page_title }}
          </span>
          <button
            type="button"
            class="btn btn-link text-muted p-0"
            :aria-label="is_summary_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
            :title="is_summary_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
            @click="toggle_summary_detail_fullscreen"
          >
            <i :class="['bi', is_summary_detail_fullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen', 'fs-5']"></i>
          </button>
          <button type="button" class="btn btn-link text-muted p-0" aria-label="Close entity detail" @click="close_case_summary">
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>
    </header>

    <div class="onboarding-case-detail-body p-4">
      <nav class="bg-light rounded-3 p-1 d-flex align-items-center gap-2 mb-4" aria-label="Status queue detail sections">
        <button
          v-for="tab in summary_detail_tabs"
          :key="tab.id"
          type="button"
          :class="[
            'btn btn-sm flex-fill d-inline-flex align-items-center justify-content-center gap-2',
            active_summary_detail_tab === tab.id ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'
          ]"
          @click="set_summary_detail_tab(tab.id)"
        >
          <i :class="['bi', tab.icon]"></i>
          <span>{{ tab.label }}</span>
          <span v-if="tab.count" class="badge rounded-pill text-bg-danger">{{ tab.count }}</span>
        </button>
      </nav>
      <section v-if="active_summary_detail_tab === 'summary'">
        <div v-if="is_loading_summary_detail" class="text-muted mb-4">Loading entity detail...</div>

        <dl class="row g-0 mb-4">
          <template v-for="row in summary_rows" :key="row.label">
            <dt class="col-4 text-muted fw-normal mb-3">{{ row.label }}</dt>
            <dd class="col-8 fw-semibold mb-3">{{ row.value }}</dd>
          </template>
        </dl>

        <section>
          <div class="text-uppercase text-muted fw-semibold small mb-3">Stage timeline</div>
          <div class="onboarding-case-timeline">
            <article
              v-for="item in summary_stage_timeline"
              :key="item.title"
              :class="['onboarding-case-timeline-item', `is-${item.status}`]"
            >
              <span class="onboarding-case-timeline-icon">
                <i :class="['bi', item.icon]"></i>
              </span>
              <div>
                <div class="fw-semibold text-black">{{ item.title }}</div>
                <div class="text-muted small mt-1">{{ item.detail }}</div>
              </div>
            </article>
          </div>
        </section>
      </section>

      <section v-else-if="active_summary_form_section">
        <div class="text-uppercase text-muted fw-semibold small mb-3">{{ active_summary_form_section.label }}</div>
        <section
          v-if="selected_document_preview"
          class="onboarding-document-preview mb-4"
        >
          <header class="onboarding-document-preview__header">
            <div class="min-w-0">
              <p class="onboarding-document-preview__eyebrow mb-1">Document preview</p>
              <h3 class="onboarding-document-preview__title">{{ selected_document_preview.title }}</h3>
            </div>
            <button
              type="button"
              class="onboarding-document-preview__close"
              aria-label="Close document preview"
              @click="close_document_preview"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </header>
          <iframe
            class="onboarding-document-preview__frame"
            :src="selected_document_preview.url"
            :title="selected_document_preview.title"
          ></iframe>
        </section>
        <section
          v-for="sub_section in active_summary_form_section.sub_sections"
          :key="sub_section.id"
          class="mb-4"
        >
          <div
            v-if="sub_section.label && sub_section.label !== active_summary_form_section.label"
            class="text-uppercase text-muted fw-semibold small mb-3"
          >
            {{ sub_section.label }}
          </div>
          <div class="onboarding-answer-grid">
            <article
              v-for="question in sub_section.questions"
              :key="question.id"
              class="onboarding-answer-card"
            >
              <p class="onboarding-answer-card__label">
                {{ question.label }}
                <span v-if="question.is_mandatory" class="text-danger">*</span>
              </p>
              <div class="onboarding-answer-card__value d-flex align-items-center justify-content-between gap-2">
                <span>{{ question.answer }}</span>
                <button
                  v-if="question.file_url"
                  type="button"
                  class="onboarding-document-view-button d-inline-flex align-items-center gap-1 flex-shrink-0"
                  @click="open_question_document(question)"
                >
                  <span>View</span>
                </button>
              </div>
            </article>
          </div>
        </section>
      </section>
    </div>

    <footer class="onboarding-case-detail-footer border-top bg-white p-3 d-flex align-items-center justify-content-between gap-3">
      <button type="button" class="btn btn-link text-muted d-inline-flex align-items-center gap-2 text-decoration-none">
        <i class="bi bi-stars"></i>
        <span>Ask AI</span>
      </button>

      <div class="d-flex align-items-center gap-2">
          <button
          type="button"
          class="btn btn-outline-danger d-inline-flex align-items-center gap-2"
          :disabled="!can_reject_stage || workflow_action_in_progress === 'reject' || workflow_actions_loading"
          @click="run_selected_workflow_action('reject')"
        >
          <i class="bi bi-x-circle"></i>
          <span>{{ workflow_action_in_progress === 'reject' ? 'Rejecting...' : 'Reject' }}</span>
        </button>

        <button
          type="button"
          class="btn btn-outline-warning d-inline-flex align-items-center gap-2"
          :disabled="!can_request_info || workflow_action_in_progress === 'request_info' || workflow_actions_loading"
          @click="run_selected_workflow_action('request_info')"
        >
          <i class="bi bi-send"></i>
          <span>{{ workflow_action_in_progress === 'request_info' ? 'Sending...' : 'Request info' }}</span>
        </button>

        <button
          v-if="can_advance_stage"
          type="button"
          class="btn btn-primary d-inline-flex align-items-center gap-2"
          :disabled="workflow_action_in_progress === 'advance' || workflow_actions_loading"
          @click="run_selected_workflow_action('advance')"
        >
          <i class="bi bi-arrow-right"></i>
          <span>{{ workflow_action_in_progress === 'advance' ? 'Advancing...' : 'Advance stage' }}</span>
        </button>
      </div>
    </footer>
    <div v-if="workflow_actions_loading || workflow_actions_error || workflow_action_feedback" class="px-3 pb-3">
      <div v-if="workflow_actions_loading" class="small text-muted">Loading workflow actions...</div>
      <div v-else-if="workflow_actions_error" class="alert alert-warning mb-0 py-2 px-3 small">{{ workflow_actions_error }}</div>
      <div
        v-else-if="workflow_action_feedback"
        :class="['alert mb-0 py-2 px-3 small', workflow_action_feedback_tone === 'success' ? 'alert-success' : workflow_action_feedback_tone === 'danger' ? 'alert-danger' : 'alert-warning']"
      >
        {{ workflow_action_feedback }}
      </div>
    </div>
  </aside>
</main>
